/**
 * Staff composer: public reply or internal note. The mode restyles the whole
 * box, not just a checkbox — getting it wrong either leaks an internal
 * discussion or leaves the customer waiting on a reply nobody sent.
 */

import { useState, type KeyboardEvent } from 'react';
import { Lock, Send, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { SUPPORT_BODY_MAX, type SupportMessageVisibility } from '@repo/types';

export function ReplyBox({
  onSend,
  sending,
  disabled,
  disabledReason,
}: {
  onSend: (body: string, visibility: SupportMessageVisibility) => Promise<unknown>;
  sending: boolean;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const [visibility, setVisibility] = useState<SupportMessageVisibility>('public');
  const [body, setBody] = useState('');

  const internal = visibility === 'internal';
  const trimmed = body.trim();
  const tooLong = trimmed.length > SUPPORT_BODY_MAX;
  // A closed ticket still accepts internal notes — staff need to be able to
  // annotate what happened after the fact.
  const blocked = Boolean(disabled) && !internal;
  const canSend = trimmed.length > 0 && !tooLong && !sending && !blocked;

  async function send() {
    if (!canSend) return;
    try {
      // Cleared only on success, so a failed send never loses what was typed —
      // which matters most for a long internal note.
      await onSend(trimmed, visibility);
      setBody('');
    } catch {
      // Already surfaced by the mutation's onError. Caught so the keydown
      // handler's `void send()` cannot raise an unhandled rejection.
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void send();
    }
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-3 transition-colors',
        internal ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-card'
      )}
    >
      <div className="mb-2.5 flex items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant={internal ? 'ghost' : 'secondary'}
          onClick={() => setVisibility('public')}
          className="h-7 gap-1.5 text-xs"
        >
          <Users className="size-3.5" />
          Reply to customer
        </Button>
        <Button
          type="button"
          size="sm"
          variant={internal ? 'secondary' : 'ghost'}
          onClick={() => setVisibility('internal')}
          className={cn('h-7 gap-1.5 text-xs', internal && 'text-amber-700 dark:text-amber-500')}
        >
          <Lock className="size-3.5" />
          Internal note
        </Button>
      </div>

      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={onKeyDown}
        rows={4}
        disabled={blocked}
        placeholder={
          internal
            ? 'Context for the team. The customer will never see this.'
            : 'Write a reply the customer will see…'
        }
        className="resize-y text-sm"
        aria-label={internal ? 'Internal note' : 'Reply to customer'}
      />

      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {blocked && disabledReason ? (
            <span className="text-amber-600">{disabledReason}</span>
          ) : internal ? (
            <span className="flex items-center gap-1 text-amber-700 dark:text-amber-500">
              <Lock className="size-3" />
              Staff only. The customer cannot see this.
            </span>
          ) : (
            'This goes to the customer. ⌘/Ctrl + Enter to send.'
          )}
        </p>

        <div className="flex items-center gap-3">
          {tooLong && (
            <span className="text-xs text-destructive">
              {trimmed.length.toLocaleString()} / {SUPPORT_BODY_MAX.toLocaleString()}
            </span>
          )}
          <Button type="button" size="sm" onClick={() => void send()} disabled={!canSend}>
            <Send className="size-3.5" />
            {internal ? 'Add note' : 'Send reply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
